/** Automatic quality levels with warm-up, averaging window, hold time and an anti-oscillation rule. Pure. */
import type { QualityLevel } from '../app/urlParams';

export const LEVELS: QualityLevel[] = ['low', 'med', 'high'];

export interface GovernorOptions {
  warmupMs: number;
  windowFrames: number;
  /** Average frame time above this drops a level (~45 fps). */
  downMs: number;
  /** Average frame time below this raises a level (~80 fps). */
  upMs: number;
  /** Minimum time between changes. */
  holdMs: number;
}

export const DEFAULT_GOVERNOR: GovernorOptions = { warmupMs: 3_000, windowFrames: 90, downMs: 22, upMs: 12, holdMs: 4_000 };

export class QualityGovernor {
  private level: number;
  private readonly startMs: number;
  private lastChangeMs = -Infinity;
  private downgrades = 0;
  private window: number[] = [];

  constructor(
    start: QualityLevel,
    startMs: number,
    private readonly locked = false,
    private readonly opts: GovernorOptions = DEFAULT_GOVERNOR,
  ) {
    this.level = LEVELS.indexOf(start);
    this.startMs = startMs;
  }

  get current(): QualityLevel {
    return LEVELS[this.level]!;
  }

  /** Feed one frame; returns the new level when it changes, else null. */
  frame(nowMs: number, frameMs: number): QualityLevel | null {
    if (this.locked || nowMs - this.startMs < this.opts.warmupMs || !Number.isFinite(frameMs)) return null;
    this.window.push(frameMs);
    if (this.window.length < this.opts.windowFrames) return null;
    const avg = this.window.reduce((s, v) => s + v, 0) / this.window.length;
    this.window = [];
    if (nowMs - this.lastChangeMs < this.opts.holdMs) return null;
    if (avg > this.opts.downMs && this.level > 0) {
      this.level--;
      this.downgrades++;
    } else if (avg < this.opts.upMs && this.level < LEVELS.length - 1 && this.downgrades < 2) {
      this.level++;
    } else {
      return null;
    }
    this.lastChangeMs = nowMs;
    return this.current;
  }
}
